import { useParams, useNavigate, Link } from "react-router"
import { useCallback, useMemo } from "react"
import { useAppSelector } from "../../hooks/reduxToolkit"
import { ExpenseModel } from "../../reduxToolkit/expensesSlice"
import { selectLinkCandidates } from "../../../lib/expenses/linkCandidates"
import { useExpenseSearch } from "../../hooks/useExpenseSearch"
import { useLinkDraftToExpense } from "../../hooks/useLinkDraftToExpense"
import { formatCurrency } from "../../../lib/formatCurrency"
import { formatDateInTimeZone } from "../../../lib/formatDateInTimeZone"
import { present } from "../../../lib/util/present"

// Reuses the receipt "attach to expense" page styling so the draft-linking flow
// looks and behaves like a full page (expense card picker) rather than a modal.
import '../receipts/attachToExpense.scss'

/**
 * Full-page picker for linking a Plaid-imported draft expense onto an existing
 * expense — the page-based replacement for the old LinkToExpenseModal. The
 * draft's bank charge is recorded as a payment on the chosen expense and the
 * draft is discarded (see useLinkDraftToExpense). No optimistic mutation happens
 * here; the list/target update arrive on the next sync-down.
 */
export function LinkDraftToExpense() {
  const { draftId } = useParams<{ draftId: string }>()
  const navigate = useNavigate()
  const allExpenses = useAppSelector((s) => s.expenses.expenses)
  const incidents = useAppSelector((s) => s.incidents.incidents)

  const draft = allExpenses.find((e) => e.id === draftId)

  const candidates = useMemo(
    () => (draft ? selectLinkCandidates(allExpenses, draft) : []),
    [allExpenses, draft],
  )
  const [results, search, { query }] = useExpenseSearch(candidates)
  const shown = results ?? candidates

  const [linkResult, link] = useLinkDraftToExpense()

  const handleExpenseSelect = useCallback(async (expense: ExpenseModel) => {
    if (!draft || linkResult.loading) { return }
    try {
      await link({ draftId: draft.id, targetId: expense.id })
      // The draft removal and the target's appended payment arrive via sync-down.
      navigate('/expenses', { state: { expense_id: expense.id } })
    } catch {
      // useAsyncAction already captured this in Sentry and set linkResult.error;
      // swallow the re-thrown rejection so it doesn't leak as an unhandled
      // rejection, and stay on the page so the error message is shown.
    }
  }, [draft, link, linkResult.loading, navigate])

  if (!draft) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger mb-3">
              Draft expense not found
            </div>
            <Link to="/expenses" className="btn btn-primary">
              Back to Expenses
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container attach-to-expense">
      <div className="row">
        <div className="col-12">
          <div className="mb-4">
            <Link to="/expenses" className="btn btn-link p-0 mb-2 d-inline-flex align-items-center">
              <i className="material-icons" style={{ fontSize: '20px' }}>arrow_back</i>
              <span className="ms-1">Back to Expenses</span>
            </Link>
            <h1>Link to existing expense</h1>
            <p className="text-muted mb-2">
              This bank charge ({draft.provider || 'unknown provider'}
              {present(draft.paidAmount) ? ` · ${formatCurrency(draft.paidAmount)}` : ''}) will be
              recorded as a payment on the expense you choose, and this draft will be removed.
            </p>
          </div>

          {linkResult.loading && (
            <div className="alert alert-info">
              <div className="d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Linking…</span>
                </div>
                Linking draft to expense…
              </div>
            </div>
          )}

          {linkResult.error && (
            <div className="alert alert-danger">
              Could not link this expense. Please try again.
            </div>
          )}

          {candidates.length === 0 ? (
            <div className="empty-state text-center py-5">
              <i className="material-icons mb-3" style={{ fontSize: '48px' }}>receipt_long</i>
              <h2>No Expenses to Link To</h2>
              <p className="text-muted">
                You don't have any incident-assigned expenses to link this bank charge to.
                Assign an expense to an incident first.
              </p>
              <Link className="btn btn-primary" to="/expenses">
                Back to Expenses
              </Link>
            </div>
          ) : (
            <>
              <p className="mb-3">
                Select an expense below to record this bank charge as a payment on it.
              </p>

              <input
                className="form-control mb-3"
                type="search"
                placeholder="Search by provider, date, amount, or patient…"
                aria-label="Search expenses"
                value={query || ''}
                onChange={(e) => search(e.target.value)}
              />

              {shown.length === 0 ? (
                <div className="text-center text-muted py-4">
                  No matching expenses.
                </div>
              ) : (
                <div className="expense-selection-list">
                  {shown.map(expense => {
                  const incident = incidents.find((i) => i.id === expense.incident_id)
                  return (
                    <div
                      key={expense.id}
                      className={`expense-card card mb-3${linkResult.loading ? ' opacity-50 pe-none' : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-disabled={linkResult.loading}
                      aria-label={`Select expense${expense.provider ? ` from ${expense.provider}` : ''}${expense.date ? ` on ${formatDateInTimeZone(expense.date, { format: 'MMM d, yyyy' })}` : ''}`}
                      onClick={() => handleExpenseSelect(expense)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          handleExpenseSelect(expense)
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <strong>
                                {expense.date ? formatDateInTimeZone(expense.date, {
                                  format: 'MMM d, yyyy'
                                }) : 'No date'}
                              </strong>
                              <div className="text-end">
                                {present(expense.listedAmount) && (
                                  <div className="text-success fw-bold">
                                    {formatCurrency(expense.listedAmount)}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6">
                                {expense.patient_name && (
                                  <div className="text-muted small">
                                    <strong>Patient:</strong> {expense.patient_name}
                                  </div>
                                )}
                                {expense.provider && (
                                  <div className="text-muted small">
                                    <strong>Provider:</strong> {expense.provider}
                                  </div>
                                )}
                              </div>
                              <div className="col-md-6">
                                {expense.incident_id && (
                                  <div className="text-muted small">
                                    <strong>Incident:</strong>{' '}
                                    {incident?.description || `#${expense.incident_id.slice(-8)}`}
                                  </div>
                                )}
                                {present(expense.paidAmount) && (
                                  <div className="text-muted small">
                                    <strong>Paid:</strong> {formatCurrency(expense.paidAmount)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="ms-3">
                            <i className="material-icons text-muted">arrow_forward</i>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
